import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, List, SlidersHorizontal } from "lucide-react";
import AdCard, { AdSkeleton } from "../components/site/AdCard";
import LocationPicker from "../components/site/LocationPicker";
import { http, useMeta } from "../lib/api";
import { CATEGORIES, CATEGORY_INFO, QUICK_DISTRICTS, PROPERTY_TYPES } from "../lib/categories";

function CategorySidebar({ active, onPick, counts }) {
  const item = (val, label, testId, Icon) => (
    <button key={testId} onClick={() => onPick(val)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${active === val ? "bg-[var(--navy)] text-white" : "hover:bg-slate-100 text-slate-700"}`} data-testid={testId}>
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="flex-1">{label}</span>
      {val && counts?.[val] ? <span className="text-xs opacity-70">{counts[val]}</span> : null}
    </button>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1" data-testid="buy-category-filter">
      <p className="px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-500">Category · श्रेणी</p>
      {item("", "सर्व · All Categories", "buy-cat-all")}
      {CATEGORIES.map((c) => item(c, `${c} · ${CATEGORY_INFO[c].mr}`, `buy-cat-${c.toLowerCase()}`, CATEGORY_INFO[c].icon))}
    </div>
  );
}

export default function Buy() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "", category = params.get("category") || "", location = params.get("location") || "", taluka = params.get("taluka") || "", place = params.get("place") || "", condition = params.get("condition") || "", ptype = params.get("ptype") || "";
  const sort = params.get("sort") || "newest";
  const page = Number(params.get("page") || 1);
  const [qInput, setQInput] = useState(q);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState(true);
  const [showCats, setShowCats] = useState(false);
  const meta = useMeta();

  const update = (patch) => {
    const next = { q, category, location, taluka, place, condition, ptype, sort, page: 1, ...patch };
    if (next.category !== "Property") next.ptype = "";
    setParams(Object.fromEntries(Object.entries(next).filter(([k, v]) => v && !(k === "page" && v === 1) && !(k === "sort" && v === "newest"))));
  };

  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    const ctrl = new AbortController();
    setData(null);
    http.get("/ads", { signal: ctrl.signal, params: { q: q || undefined, category: category || undefined, location: location || undefined, taluka: taluka || undefined, place: place || undefined, condition: condition || undefined, ptype: ptype || undefined, sort, page, limit: 20 } })
      .then((r) => setData(r.data))
      .catch(() => { if (!ctrl.signal.aborted) setData({ items: [], total: 0, pages: 1 }); });
    return () => ctrl.abort();
  }, [q, category, location, taluka, place, condition, ptype, sort, page]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-5 space-y-3" data-testid="buy-top-filters">
        <form onSubmit={(e) => { e.preventDefault(); update({ q: qInput.trim() }); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-3 top-3.5 text-slate-400" />
            <input className="field pl-10" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="संपूर्ण महाराष्ट्रात शोधा - Mobile, Car, Job..." data-testid="buy-search-input" />
          </div>
          <button className="h-12 px-5 rounded-lg bg-[var(--buy)] text-white font-semibold hover:bg-[#0d47a1] transition-colors" data-testid="buy-search-button">शोधा</button>
        </form>
        <LocationPicker withPlace freePlace district={location} taluka={taluka} place={place} onChange={(d, t, p) => update({ location: d, taluka: t, place: p })} testPrefix="buy" />
        <div className="flex flex-wrap gap-2">
          <button onClick={() => update({ location: "", taluka: "", place: "" })} className={`px-3 py-1 rounded-full text-sm border transition-colors ${!location ? "bg-[var(--orange)] border-[var(--orange)] text-white" : "border-slate-200 hover:border-[var(--orange)]"}`} data-testid="buy-quick-all">All Maharashtra</button>
          {QUICK_DISTRICTS.map((d) => (
            <button key={d} onClick={() => update({ location: d, taluka: "", place: "" })} className={`px-3 py-1 rounded-full text-sm border transition-colors ${location === d ? "bg-[var(--orange)] border-[var(--orange)] text-white" : "border-slate-200 hover:border-[var(--orange)]"}`} data-testid={`buy-quick-${d.toLowerCase().replace(/\s+/g, "-")}`}>{d}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-6">
        <aside>
          <button onClick={() => setShowCats(!showCats)} className="lg:hidden w-full mb-3 h-11 rounded-lg border border-slate-200 bg-white flex items-center justify-center gap-2 font-semibold" data-testid="buy-toggle-categories"><SlidersHorizontal className="w-4 h-4" /> Category {category && `: ${category}`}</button>
          <div className={`${showCats ? "block" : "hidden"} lg:block lg:sticky lg:top-24`}>
            <CategorySidebar active={category} counts={meta?.counts} onPick={(c) => { update({ category: c }); setShowCats(false); }} />
          </div>
        </aside>
        <section>
          {category === "Property" ? (
            <div className="mb-4 rounded-xl bg-green-50 border-2 border-green-600 p-3" data-testid="buy-ptype-filter">
              <p className="text-sm font-bold text-green-800 mb-2">नो ब्रोकर - प्रॉपर्टी डायरेक्ट मालकाकडून</p>
              <div className="flex flex-wrap gap-2">
                {[["", "सर्व प्रॉपर्टी"], ...Object.entries(PROPERTY_TYPES).map(([k, v]) => [k, v.mr])].map(([k, l]) => (
                  <button key={k || "all"} onClick={() => update({ ptype: k })} className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors ${ptype === k ? "bg-green-600 border-green-600 text-white" : "bg-white border-slate-200 hover:border-green-600"}`} data-testid={`buy-ptype-${k || "all"}`}>{l}</button>
                ))}
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-3 gap-2 mb-4 p-1 rounded-xl bg-white border border-slate-200" data-testid="buy-condition-tabs">
            {[["", "सर्व बाजार"], ["old", "जुना बाजार"], ["new", "नवीन बाजार"]].map(([v, l]) => (
              <button key={v || "all"} onClick={() => update({ condition: v })} className={`h-10 rounded-lg text-sm font-semibold transition-colors ${condition === v ? (v === "new" ? "bg-[var(--buy)] text-white" : "bg-[var(--orange)] text-white") : "text-slate-600 hover:bg-slate-100"}`} data-testid={`buy-condition-${v || "all"}`}>{l}</button>
            ))}
          </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600" data-testid="buy-results-count">{data ? <><b className="text-[var(--navy)]">{data.total.toLocaleString("en-IN")}</b> जाहिराती {location && `· ${place || taluka || location}`}</> : "Loading..."}</p>
            <div className="flex items-center gap-2">
              <select className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm" value={sort} onChange={(e) => update({ sort: e.target.value })} data-testid="buy-sort-select">
                <option value="newest">Newest · नवीन</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
              </select>
              <button onClick={() => setRows(!rows)} className="h-10 w-10 rounded-lg border border-slate-300 bg-white flex items-center justify-center" data-testid="buy-view-toggle">{rows ? <LayoutGrid className="w-4 h-4" /> : <List className="w-4 h-4" />}</button>
            </div>
          </div>
          <div className={rows ? "space-y-3" : "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"} data-testid="buy-ads-list">
            {data ? data.items.map((ad) => <AdCard key={ad.id} ad={ad} row={rows} />) : Array.from({ length: 6 }).map((_, i) => <AdSkeleton key={i} />)}
          </div>
          {data && data.items.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500" data-testid="buy-no-results">या फिल्टरसाठी जाहिराती सापडल्या नाहीत. Try another search.</div>}
          {data && data.pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3" data-testid="buy-pagination">
              <button disabled={page <= 1} onClick={() => update({ page: page - 1 })} className="h-10 px-4 rounded-lg border bg-white disabled:opacity-40" data-testid="buy-prev-page">← मागे</button>
              <span className="text-sm text-slate-600">Page {page} / {data.pages}</span>
              <button disabled={page >= data.pages} onClick={() => update({ page: page + 1 })} className="h-10 px-4 rounded-lg border bg-white disabled:opacity-40" data-testid="buy-next-page">पुढे →</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
